import { type CurrencyCodes } from 'shared/constants/currency'
import fetch, { baseClient } from './fetch'
import internalApiClient from 'shared/api/internalApiClient'
const client = fetch()
const transferClient = baseClient({})
export interface FeaturePlan {
  name: string
  reportingSolutions: {
    enabled: boolean
    price: number
    priceCurrency: string
    arimaPlusForecastingReport: boolean
    blackFridayReport: boolean
    channelGroupingPerformanceReportV2: boolean
    dv360PerformanceReport: boolean
    facebookVisualCreativeReport: boolean
    googleAdsKeywordQualityScoreDashboard: boolean
    performanceMaxBreakdownReportV2: boolean
    productMixModelingReport: boolean
    searchSynergiesReport: boolean
    shoppingInsightsBestsellerReport: boolean
    shoppingInsightsReportV2: boolean
    spendImpactBrandAnalysisReport: boolean
  }
  connectors: {
    enabled: boolean
    price: number
    priceCurrency: string
    dv360: boolean
    facebook: boolean
    googleAds: boolean
    ga4: boolean
    googleAnalytics: boolean
    merchantCenter: boolean
    googleSearchConsole: boolean
    googleSheets: boolean
    linkedin: boolean
    microsoftAdvertising: boolean
    shopify: boolean
    snapchat: boolean
    tiktok: boolean
    pinterestAds: boolean
    adobeAnalytics: boolean
    supermetrics: boolean
  }
  budgetOptimiser: {
    enabled: boolean
    price: number
    priceCurrency: string
  }
  attributionModels: {
    enabled: boolean
    price: number
    priceCurrency: string
    ddaV2: boolean
    integratedAttribution: boolean
    rba: boolean
  }
  enhancedSignals: {
    enabled: boolean
    price: number
    priceCurrency: string
    transformations: {
      profit: boolean
    }
    targets: {
      ga4MpGtagPrecisEcomm: boolean
      ga4MpGtagPurchase: boolean
      ga4MpFirebasePurchase: boolean
      ga4MpFirebasePrecisEcomm: boolean
      gadsClickConversions: boolean
      gadsRestateConversions: boolean
      sgtm: boolean
      metaConversion: boolean
    }
  }
  creativeAssets: {
    enabled: boolean
    price: number
    priceCurrency: string
  }
}

export type ClientFeatureType =
  | 'reportingSolutions'
  | 'connectors'
  | 'budgetOptimiser'
  | 'attributionModels'
  | 'enhancedSignals'
  | 'creativeAssets'

export interface ClientReq {
  name: string
  url: string
  currency?: CurrencyCodes
}

export interface ClientResp {
  name: string
  url: string
  avatarUrl?: string
  id: number
  currency: CurrencyCodes
  isSoloAdmin?: boolean
  featurePlan: FeaturePlan
}

export interface UserDetailsResp {
  system_role: string
  name: string
  email: string
  phone: string
  companyName: string
  jobTitle: string
  avatarUrl?: string
  createdAt: string
  updatedAt: string
  id: number
  showInternalFeatures: boolean
  hasInternalFeatureAccess: boolean
}

export const paths = {
  admin: '/admin',
  clients: '/clients'
}

export const adminPaths = {
  clients: '/admin'
}

export interface ClientsResp extends Array<ClientResp> {}

export const createClient = async (clients: ClientReq): Promise<ClientResp> => {
  return await client.post<ClientResp>({
    path: paths.clients,
    data: clients
  })
}

export const getClients = async (): Promise<ClientsResp> => {
  return await client.get<ClientsResp>({
    path: paths.clients
  })
}

export const getClientsWithField = async (field: string): Promise<ClientsResp> => {
  return await client.get<ClientsResp>({
    path: `${paths.clients}?fields=${field}`
  })
}

type userRole = 'viewer' | 'manager' | 'editor'

export interface MembersItem {
  name: string
  email: string
  id: number
  role: userRole
  createdAt: string
  updatedAt: string
}
export interface MembersResp extends Array<MembersItem> {}

export const getMembers = async (clientId: number): Promise<MembersResp> => {
  return await client.get<MembersResp>({
    path: `/clients/${clientId}/users`
  })
}

export type Severity = 'critical' | 'moderate' | 'low'

export interface NotificationSettings {
  datasources: {
    on: boolean
    severity: Severity[]
  }
  job_statuses: {
    attribution_models: {
      on: boolean
    }
    connectors: {
      on: boolean
    }
    on: boolean
    reporting_solutions: {
      on: boolean
    }
    budget_optimiser: {
      on: boolean
    }
    enhanced_signals: {
      on: boolean
    }
    severity: Severity[]
  }
  recommendations: {
    on: boolean
    severity: Severity[]
  }
}

export type MemberRole = 'viewer' | 'editor' | 'admin'
export interface MemberResp {
  name: string
  email: string
  id: number
  role: MemberRole
  phone?: string
  companyName?: string
  jobTitle?: string
  notificationSettings: NotificationSettings
}

export const getMember = async (clientId: number, userId: number): Promise<MemberResp> => {
  return await client.get<MemberResp>({
    path: `/clients/${clientId}/users/${userId}`
  })
}

export interface PatchNotificationSettingsReq {
  clientId: number
  userId: number
  notificationSettings: NotificationSettings
}

export const updateMemberNotificationSettings = async (data: PatchNotificationSettingsReq): Promise<MemberResp> => {
  const { clientId, userId, notificationSettings } = data
  return await client.patch<MemberResp>({
    path: `/clients/${clientId}/users/${userId}`,
    data: { notificationSettings }
  })
}

export interface PatchMemberReq {
  clientId: number
  userId: number
  role: string
}

export const updateMember = async (data: PatchMemberReq): Promise<MemberResp> => {
  const { clientId, userId, role } = data
  return await client.patch<MemberResp>({
    path: `/clients/${clientId}/users/${userId}`,
    data: { role }
  })
}
export interface PatchClientReq {
  name?: string
  url?: string
  id: number
  currency?: string
}

export const updateClient = async (data: PatchClientReq): Promise<ClientResp> => {
  const { id: clientId, ...clientDetailsToUpdate } = data
  return await client.patch<ClientResp>({
    path: `/clients/${clientId}`,
    data: clientDetailsToUpdate
  })
}

export interface DeleteMemberReq {
  clientId: number
  userId: number
}

export const deleteMember = async (data: DeleteMemberReq): Promise<MemberResp> => {
  const { clientId, userId } = data
  return await client.del<MemberResp>({
    path: `/clients/${clientId}/users/${userId}`
  })
}

export interface PatchUpdateMemberRoleReq {
  clientId: number
  userId: number
  role: string
}

export const updateMemberRole = async (data: PatchUpdateMemberRoleReq): Promise<MemberResp> => {
  const { clientId, userId, role } = data
  return await client.patch<MemberResp>({
    path: `/clients/${clientId}/users/${userId}`,
    data: {
      role
    }
  })
}

export interface DeleteClientResp {
  id: number
  status: string
  url?: string
}

export interface DeleteClientReq {
  clientId: number
}

export const deleteClient = async (data: DeleteClientReq): Promise<DeleteClientResp> => {
  const { clientId } = data
  return await client.del<DeleteClientResp>({
    path: `/clients/${clientId}`
  })
}

export const getUserDetailsByEmail = async (userEmail: string, clientId: number): Promise<UserDetailsResp | null> => {
  const response = await client.get<UserDetailsResp[]>({
    path: `${paths.clients}/${clientId}/users?email=${userEmail}`
  })
  return response.length === 1 ? response[0] : null
}

export interface ExternalAccessKeyResp {
  accessKey: string
}

type ExternalAccessKeyPlatform = 'supermetrics'

export const getExternalAccessKeys = async ({
  clientId,
  platform,
  token
}: {
  clientId: number
  platform: ExternalAccessKeyPlatform
  token: string
}): Promise<ExternalAccessKeyResp> => {
  return await internalApiClient().get<ExternalAccessKeyResp>({
    path: `${paths.admin}${paths.clients}/${clientId}/external_access_keys?platform=${platform}`,
    token
  })
}

export const getClient = async (clientId: number): Promise<ClientResp> => {
  return await client.get<ClientResp>({
    path: `/clients/${clientId}`
  })
}

export const getAvatarSignedUrl = async (clientId: number): Promise<{ signedUrl: string }> => {
  return await client.get<{ signedUrl: string }>({
    path: `/clients/${clientId}/avatar/signed-url`
  })
}

export const uploadAvatar = async ({ file, signedURL }: { file: File; signedURL: string }): Promise<string> => {
  return await transferClient.put<string>({
    path: signedURL,
    data: file
  })
}
